/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useMemo, useRef, useState } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { Menu, PLACEMENT, Popup } from '@web-stories-wp/design-system';
import { useFeature } from 'flagged';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

/**
 * Internal dependencies
 */
import { useCanvas, useStory } from '../../app';
import { ReactComponent as SafeZonesDisabledIconSvg } from '../canvas/safeZones/assets/safe_zones_disabled.svg';
import { ReactComponent as SafeZonesEnabledIconSvg } from '../canvas/safeZones/assets/safe_zones-enabled.svg';
import {
  getSafeZonePresetOptionsForAssetTypes,
  getSafeZonePresetOptions,
} from '../canvas/safeZones/safeZoneRegistry';
import useSafeZoneContext from '../canvas/safeZones/useSafeZoneContext';
import { ToggleButton } from '../toggleButton';

const SafeZoneControls = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledToggleButton = styled(ToggleButton)`
  display: flex;
  align-items: center;
  line-height: 0;
`;

const menuStylesCSS = css`
  min-width: 220px;
  margin-top: 0;

  li span {
    white-space: nowrap;
  }
`;

const MenuContainer = styled.div`
  z-index: 11;
`;

const MENU_HIDE_VALUE = '__safe_zones_hide__';
const MENU_NO_TEMPLATES_VALUE = '__safe_zones_no_templates__';
const ADMIN_AD_DEFAULT_PRESET_ID = 'safe-zone-ad';

const SafeZonesEnabledIcon = styled(SafeZonesEnabledIconSvg)`
  height: 24px;
  width: 24px;
  display: block;
`;

const SafeZonesDisabledIcon = styled(SafeZonesDisabledIconSvg)`
  height: 24px;
  width: 24px;
  display: block;
`;

const NO_SAFE_ZONE_TEMPLATES_LABEL = __('No Safe Zone Templates', 'web-stories');

function SafeZonesButton({ disabled = false }) {
  const isSafeZonesEnabled = useFeature('useStudioSafeZones');
  const {
    assetType,
    isTemplateAsset,
    safeZoneAssetSubtype,
    templateAssetTypes,
    thumbnailChipDirection,
    hasAdminAccess,
    isContentBankAsset,
    isCardsAsset,
    isAdAsset,
    resolveSafeZonesForCurrentAsset,
    getSafeZonePresetOptionsForCurrentAsset,
  } = useSafeZoneContext();
  const currentPageType = useStory(
    ({ state }) => state.currentPage?.cmsPageType ?? null
  );
  const {
    showSafeZones,
    toggleSafeZones,
    safeZonePresetId,
    setSafeZonePresetId,
  } = useCanvas(
    ({
      state: { showSafeZones, safeZonePresetId },
      actions: { toggleSafeZones, setSafeZonePresetId },
    }) => ({
      showSafeZones,
      toggleSafeZones,
      safeZonePresetId,
      setSafeZonePresetId,
    })
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleButtonRef = useRef();
  const menuWasOpenOnMouseDownRef = useRef(false);

  const assetSafeZonePresetOptions = useMemo(
    () => getSafeZonePresetOptionsForCurrentAsset({ cmsPageType: currentPageType }),
    [currentPageType, getSafeZonePresetOptionsForCurrentAsset]
  );

  const adminSafeZonePresetOptions = useMemo(
    () => {
      if (isTemplateAsset) {
        return getSafeZonePresetOptionsForAssetTypes(templateAssetTypes);
      }

      const ratioAssetType =
        isContentBankAsset || isCardsAsset ? safeZoneAssetSubtype : assetType;
      const ratioMatchedPresetOptions = getSafeZonePresetOptions({
        includeAllPresets: true,
        ratioAssetType,
        thumbnailChipDirection,
      });

      return ratioMatchedPresetOptions.length
        ? ratioMatchedPresetOptions
        : assetSafeZonePresetOptions;
    },
    [
      assetSafeZonePresetOptions,
      assetType,
      isTemplateAsset,
      isContentBankAsset,
      isCardsAsset,
      safeZoneAssetSubtype,
      templateAssetTypes,
      thumbnailChipDirection,
    ]
  );

  const nonAdminContentBankPresetOptions = useMemo(
    () =>
      getSafeZonePresetOptions({
        includeAllPresets: true,
        ratioAssetType: safeZoneAssetSubtype,
        defaultOnlyByRatio: true,
        thumbnailChipDirection,
      }),
    [safeZoneAssetSubtype, thumbnailChipDirection]
  );

  const shouldAllowPresetSelection =
    hasAdminAccess ||
    isContentBankAsset ||
    (isAdAsset && assetSafeZonePresetOptions.length > 1);

  const selectableSafeZonePresetOptions = useMemo(
    () => {
      if (hasAdminAccess) {
        return adminSafeZonePresetOptions;
      }

      if (isContentBankAsset) {
        return nonAdminContentBankPresetOptions;
      }

      if (isAdAsset) {
        return assetSafeZonePresetOptions;
      }

      return [];
    },
    [
      adminSafeZonePresetOptions,
      assetSafeZonePresetOptions,
      hasAdminAccess,
      isAdAsset,
      isContentBankAsset,
      nonAdminContentBankPresetOptions,
    ]
  );

  const safeZonePresetOptions = useMemo(
    () => (shouldAllowPresetSelection ? selectableSafeZonePresetOptions : []),
    [shouldAllowPresetSelection, selectableSafeZonePresetOptions]
  );

  const hasSelectableSafeZoneTemplates =
    selectableSafeZonePresetOptions.length > 0;

  const defaultSafeZonePresetId = useMemo(() => {
    const adminAdDefaultPresetId =
      hasAdminAccess && isAdAsset ? ADMIN_AD_DEFAULT_PRESET_ID : '';

    if (
      adminAdDefaultPresetId &&
      selectableSafeZonePresetOptions.some(
        ({ value }) => value === adminAdDefaultPresetId
      )
    ) {
      return adminAdDefaultPresetId;
    }

    const defaultAssetPresetId = assetSafeZonePresetOptions[0]?.value;
    if (
      defaultAssetPresetId &&
      selectableSafeZonePresetOptions.some(
        ({ value }) => value === defaultAssetPresetId
      )
    ) {
      return defaultAssetPresetId;
    }

    return selectableSafeZonePresetOptions[0]?.value || '';
  }, [
    assetSafeZonePresetOptions,
    hasAdminAccess,
    isAdAsset,
    selectableSafeZonePresetOptions,
  ]);

  const selectedSafeZonePresetId = useMemo(() => {
    if (!safeZonePresetOptions.length) {
      return '';
    }

    const selectedOption = safeZonePresetOptions.find(
      ({ value }) => value === safeZonePresetId
    );

    if (selectedOption) {
      return selectedOption.value;
    }

    return defaultSafeZonePresetId;
  }, [defaultSafeZonePresetId, safeZonePresetId, safeZonePresetOptions]);

  const resolvedSafeZonePresetId = useMemo(() => {
    if (selectedSafeZonePresetId) {
      return selectedSafeZonePresetId;
    }

    return safeZonePresetId;
  }, [selectedSafeZonePresetId, safeZonePresetId]);

  const safeZones = useMemo(
    () =>
      resolveSafeZonesForCurrentAsset({
        cmsPageType: currentPageType,
        safeZonePresetId: resolvedSafeZonePresetId,
        allowPresetOverride: shouldAllowPresetSelection,
      }),
    [
      currentPageType,
      resolvedSafeZonePresetId,
      resolveSafeZonesForCurrentAsset,
      shouldAllowPresetSelection,
    ]
  );

  const hasRenderableSafeZones = safeZones.length > 0;
  const hasSafeZoneSupport =
    hasRenderableSafeZones ||
    assetSafeZonePresetOptions.length > 0 ||
    selectableSafeZonePresetOptions.length > 0;

  const canToggleSafeZones =
    !disabled &&
    (hasRenderableSafeZones ||
      hasAdminAccess ||
      isContentBankAsset ||
      hasSelectableSafeZoneTemplates);
  const shouldUsePopupMenu = shouldAllowPresetSelection;
  const shouldUseSafeZonesTooltipLabel =
    showSafeZones && shouldUsePopupMenu && hasSelectableSafeZoneTemplates;

  const label = shouldUseSafeZonesTooltipLabel
    ? __('Safe Zones', 'web-stories')
    : showSafeZones
      ? __('Hide Safe Zones', 'web-stories')
      : __('Show Safe Zones', 'web-stories');
  const SafeZonesIcon = showSafeZones
    ? SafeZonesEnabledIcon
    : SafeZonesDisabledIcon;
  const handleDirectToggle = useCallback(
    () => canToggleSafeZones && toggleSafeZones(),
    [canToggleSafeZones, toggleSafeZones]
  );
  const handleButtonMouseDownCapture = useCallback((event) => {
    menuWasOpenOnMouseDownRef.current = isMenuOpen;
    if (isMenuOpen) {
      event.preventDefault();
      event.stopPropagation();
      setIsMenuOpen(false);
    }
  }, [isMenuOpen]);
  const handleMenuToggle = useCallback(
    () => {
      if (!canToggleSafeZones) {
        return;
      }

      const wasMenuOpenOnMouseDown = menuWasOpenOnMouseDownRef.current;
      menuWasOpenOnMouseDownRef.current = false;

      if (wasMenuOpenOnMouseDown || isMenuOpen) {
        setIsMenuOpen(false);
        return;
      }

      const hasValidSelectedPreset = selectableSafeZonePresetOptions.some(
        ({ value }) => value === safeZonePresetId
      );
      const shouldAutoSelectDefaultPreset =
        !showSafeZones &&
        hasSelectableSafeZoneTemplates &&
        Boolean(defaultSafeZonePresetId) &&
        !hasValidSelectedPreset;

      if (!showSafeZones && hasSelectableSafeZoneTemplates) {
        if (shouldAutoSelectDefaultPreset) {
          setSafeZonePresetId(defaultSafeZonePresetId);
        }
        toggleSafeZones();
        setIsMenuOpen(true);
        return;
      }

      setIsMenuOpen(true);
    },
    [
      canToggleSafeZones,
      isMenuOpen,
      hasSelectableSafeZoneTemplates,
      defaultSafeZonePresetId,
      safeZonePresetId,
      selectableSafeZonePresetOptions,
      setSafeZonePresetId,
      showSafeZones,
      toggleSafeZones,
    ]
  );
  const handleMenuDismiss = useCallback((event) => {
    const dismissSource =
      event?.target ||
      event?.srcElement ||
      event?.nativeEvent?.target ||
      event?.srcElement?.nearestViewportElement;

    const isDismissFromToggleButton =
      dismissSource &&
      typeof dismissSource === 'object' &&
      'nodeType' in dismissSource &&
      toggleButtonRef.current?.contains(dismissSource);

    if (isDismissFromToggleButton) {
      return;
    }

    setIsMenuOpen(false);
  }, []);
  const handleMenuItemClick = useCallback(
    (_event, value) => {
      if (value === MENU_HIDE_VALUE) {
        if (showSafeZones && canToggleSafeZones) {
          toggleSafeZones();
        }
      } else if (value && value !== MENU_NO_TEMPLATES_VALUE) {
        setSafeZonePresetId(value);
        if (!showSafeZones && canToggleSafeZones) {
          toggleSafeZones();
        }
      }

      setIsMenuOpen(false);
    },
    [
      canToggleSafeZones,
      setSafeZonePresetId,
      showSafeZones,
      toggleSafeZones,
    ]
  );

  const shouldShowPresetSelector = shouldAllowPresetSelection;
  const menuOptions = useMemo(() => {
    const options = [];

    options.push({
      label: __('Hidden', 'web-stories'),
      value: MENU_HIDE_VALUE,
    });

    if (shouldShowPresetSelector) {
      if (!hasSelectableSafeZoneTemplates) {
        options.push({
          label: NO_SAFE_ZONE_TEMPLATES_LABEL,
          value: MENU_NO_TEMPLATES_VALUE,
          disabled: true,
        });
      } else {
        options.push(...safeZonePresetOptions);
      }
    }

    return [{ group: options }];
  }, [
    hasSelectableSafeZoneTemplates,
    safeZonePresetOptions,
    shouldShowPresetSelector,
  ]);
  const menuActiveValue = showSafeZones
    ? selectedSafeZonePresetId
    : MENU_HIDE_VALUE;

  if (!isSafeZonesEnabled) {
    return null;
  }

  if (!hasSafeZoneSupport && !hasAdminAccess && !isContentBankAsset) {
    return null;
  }

  return (
    <SafeZoneControls>
      <StyledToggleButton
        ref={toggleButtonRef}
        disabled={disabled || !canToggleSafeZones}
        isOpen={showSafeZones}
        onMouseDownCapture={
          shouldUsePopupMenu ? handleButtonMouseDownCapture : undefined
        }
        onClick={shouldUsePopupMenu ? handleMenuToggle : handleDirectToggle}
        aria-label={label}
        label={label}
        tooltipDisabled={disabled}
        MainIcon={SafeZonesIcon}
        id="safe-zones-button"
        aria-owns={shouldUsePopupMenu && isMenuOpen ? 'safe-zones-menu' : null}
        aria-haspopup
        aria-expanded={shouldUsePopupMenu ? isMenuOpen : showSafeZones}
      />
      {shouldUsePopupMenu && (
        <Popup
          anchor={toggleButtonRef}
          placement={PLACEMENT.TOP_END}
          isOpen={isMenuOpen}
        >
          <MenuContainer>
            <Menu
              parentId="safe-zones-button"
              listId="safe-zones-menu"
              onMenuItemClick={handleMenuItemClick}
              options={menuOptions}
              onDismissMenu={handleMenuDismiss}
              hasMenuRole
              activeValue={menuActiveValue}
              menuStylesOverride={menuStylesCSS}
            />
          </MenuContainer>
        </Popup>
      )}
    </SafeZoneControls>
  );
}

SafeZonesButton.propTypes = {
  disabled: PropTypes.bool,
};

export default SafeZonesButton;
